/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { useServiceContext } from '../../../../root'
import { useRcmTableData } from '../../../../hooks/useRcmTableData'
import { RcmTable } from '../../../rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../../table/TableComponents'
import ActionsMenu from '../../../shared/ActionsMenu'
import TaskDocLink from '../../../shared/TaskDocLink'
import { useModal } from '~/contexts/ModalContext'
import WorkflowDetailsModal from '~/components/modals/WorkflowDetailsModal'
import WorkflowCommentsModal from '~/components/modals/WorkflowCommentsModal'
import useWorkflowsService from '~/hooks/useWorkflowsService'
import { WorkflowEditModal } from '~/components/modals/WorkflowEditModal'
import WorkflowCollaboratorsModal from '~/components/modals/WorkflowCollaboratorsModal'
import { WorkflowReviewModal } from '~/components/modals/WorkflowReviewModal'
import WorkflowEffectiveDateModal from '~/components/modals/WorkflowEffectiveDateModal'
import { WorkflowQcApproversModal } from '~/components/modals/WorkflowQcApproversModal'
import { WorkflowApproversModal } from '~/components/modals/WorkflowApproversModal'
import { WorkflowTraineesModal } from '~/components/modals/WorkflowTraineesModal'
import { WorkflowRejectionReviewersModal } from '~/components/modals/WorkflowRejectionReviewersModal'
import type { DetailedWorkflow } from '~/services/api/workflows'
import WorkflowsTableFull from '~/screens/workflows/Table'

interface WorkflowsTableProps {
  isEosEnabled: boolean
}

interface Workflow {
  id: number
  formattedId: string
  name?: string
  localizedType?: string
  workflowType?: string
  status: string
  progress?: number
  startDate: number
  initiator?: {
    name: string
  }
  currentPhase?: string
  rejected?: boolean
  studyName?: string
  flags?: WorkflowFlags

  canAddUserToNOfAllTaskGroup?: boolean
  canEditWorkflow?: boolean
  canEditWorkflowReviewers?: boolean
  canEditWorkflowApprovers?: boolean
  canEditWorkflowTrainees?: boolean
  canEditRejectionReviewers?: boolean
}

interface WorkflowFlags {
  canViewComments?: boolean
  canResume?: boolean
  canCancel?: boolean
  canUpdateEffectiveDate?: boolean
}

interface WorkflowMenuOptions {
  selectedWholeResult?: boolean
  selectedIds?: number[]
  selectedItems?: Workflow[]
  deselectAll?: () => void
}

interface MenuItem {
  value?: string
  action?: () => void
  submenu?: MenuItem[]
  divider?: boolean
  disabled?: boolean
  label?: string
}

const WorkflowStatus = {
  CANCELLED: 'CANCELLED',
  FINISHED: 'FINISHED',
  SUSPENDED: 'SUSPENDED'
}

// WorkflowTableMenu Component - Based on workflows TableMenu.jsx
const WorkflowTableMenu: React.FC<{
  item: Workflow
  options?: WorkflowMenuOptions
}> = ({ item, options = {} }) => {
  const context = useServiceContext()
  const { api } = context
  const [optionsLoaded, setOptionsLoaded] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const { showModal } = useModal()
  const { resume, cancel } = useWorkflowsService()
  const [items, setItems] = useState<MenuItem[]>([
    {
      value: 'LOADING...',
      action: () => {},
      disabled: true
    }
  ])

  const isWholeResultSelection = options?.selectedWholeResult
  const isMultiSelection = options?.selectedIds && options.selectedIds.length > 0
  const isMultiButOnlyOneSelection =
    isMultiSelection && options?.selectedIds && options.selectedIds.length === 1 && options.selectedIds[0] === item.id
  const isFsiIncludedInMultiSelection = isMultiSelection && options.selectedIds?.includes(item.id)

  const multipleWorkflowsSelection = (flow: Workflow): void => {
    const menu: MenuItem[] = []

    if (isWholeResultSelection || (isMultiSelection && !isMultiButOnlyOneSelection && flow?.flags?.canCancel)) {
      menu.push({
        value: 'ACTION_COMMAND_CANCEL',
        action: () => {
          const idsToCancel =
            options?.selectedItems && options.selectedItems.length > 1 ? options.selectedIds || [] : [item.id]
          cancel(idsToCancel, flow as import('~/screens/workflows/types').Workflow)
        }
      })
    } else if (isWholeResultSelection || (isMultiSelection && !isMultiButOnlyOneSelection && !flow?.flags?.canCancel)) {
      menu.push({
        value: 'ACTION_MENU_NO_ACTIONS_AVAILABLE',
        action: () => {},
        disabled: true
      })
    }
    setItems(menu)
  }

  const setOptions = (flow: Workflow): void => {
    const menu: MenuItem[] = []
    const subMenu: MenuItem[] = []

    menu.push({
      value: 'SHOW_DETAIL',
      action: () => {
        showModal(<WorkflowDetailsModal wfId={flow.id} />)
      }
    })

    if (flow?.flags?.canViewComments) {
      menu.push({
        value: 'VIEW_COMMENTS_ACTION',
        action: () => {
          showModal(<WorkflowCommentsModal workflow={flow} />)
        }
      })
    }

    if (flow?.flags?.canResume) {
      menu.push({
        value: 'ACTION_COMMAND_RESUME',
        action: () => {
          resume(flow.id)
        }
      })
    }

    if (flow?.flags?.canCancel) {
      menu.push({
        value: 'ACTION_COMMAND_CANCEL',
        action: () => {
          const idsToCancel =
            options?.selectedItems && options.selectedItems.length > 1 ? options.selectedIds || [] : [item.id]
          cancel(idsToCancel, flow as import('~/screens/workflows/types').Workflow)
        }
      })
    }

    if (flow?.flags?.canUpdateEffectiveDate) {
      menu.push({
        value: 'ACTION_COMMAND_UPDATE_EFFECTIVE_DATE',
        action: async () => {
          const detailed = await api.workflows.getWorkflow(flow.id)
          showModal(<WorkflowEffectiveDateModal workflowId={flow.id} workflow={detailed} />)
        }
      })
    }

    // Build submenu for management actions
    if (flow?.canAddUserToNOfAllTaskGroup) {
      subMenu.push({
        value: 'CR_ACTION_ADD_COLLABORATORS_TO_CURRENT_PHASE',
        action: async () => {
          const detailed = await api.workflows.getWorkflow(flow.id)
          showModal(<WorkflowCollaboratorsModal workflowId={flow.id} workflow={detailed} />)
        }
      })
    }

    if (flow?.canEditWorkflow) {
      subMenu.push({
        value: 'EDIT',
        action: async () => {
          const detailed = await api.workflows.getWorkflow(flow.id)
          showModal(<WorkflowEditModal workflowId={flow.id} workflow={detailed} />)
        }
      })
    }

    if (flow?.canEditWorkflowReviewers) {
      subMenu.push({
        value: 'BROWSER_ACTION_REVIEWERS',
        action: async () => {
          const detailed = await api.workflows.getWorkflow(flow.id)
          showModal(<WorkflowReviewModal workflowId={flow.id} workflow={detailed} />)
        }
      })
    }

    if (flow?.canEditWorkflowApprovers) {
      subMenu.push({
        value: flow?.workflowType === 'QC_REVIEW' ? 'WF_PHASE_QC_APPROVAL' : 'BROWSER_ACTION_APPROVERS',
        action: async () => {
          const detailed = (await api.workflows.getWorkflow(flow.id)) as DetailedWorkflow
          const isQcReview = detailed.workflowType === 'QC_REVIEW'
          if (isQcReview) {
            showModal(<WorkflowQcApproversModal workflowId={flow.id} workflow={detailed} />)
          } else {
            showModal(<WorkflowApproversModal workflowId={flow.id} workflow={detailed} />)
          }
        }
      })
    }

    if (flow?.canEditWorkflowTrainees) {
      subMenu.push({
        value: 'BROWSER_ACTION_TRAINEES',
        action: async () => {
          const detailed = await api.workflows.getWorkflow(flow.id)
          showModal(<WorkflowTraineesModal workflowId={flow.id} workflow={detailed} />)
        }
      })
    }

    if (flow?.canEditRejectionReviewers) {
      subMenu.push({
        value: 'BROWSER_ACTION_REJECTION_REVIEWERS',
        action: async () => {
          const detailed = await api.workflows.getWorkflow(flow.id)
          showModal(<WorkflowRejectionReviewersModal workflowId={flow.id} workflow={detailed} />)
        }
      })
    }

    if (subMenu.length > 0) {
      menu.push({ value: 'MANAGE', submenu: subMenu })
    }

    setItems(menu)
  }

  const handleMenuToggle = (isOpen: boolean) => {
    setIsOpen(isOpen)
    if (isOpen) {
      if (isMultiSelection && !isFsiIncludedInMultiSelection) {
        api.workflows
          .getWorkflow(item.id)
          .then((data: any) => {
            setOptions(data)
            setOptionsLoaded(true)
          })
          .catch((error: any) => {
            console.error('Failed to load workflow options:', error)
            setOptionsLoaded(true)
          })
        // if the action menu was opened for workflow which is not in the current multiselection, deselect all
        if (typeof options.deselectAll === 'function') {
          options.deselectAll()
        }
      } else if (
        isWholeResultSelection ||
        (isMultiSelection && !isMultiButOnlyOneSelection && isFsiIncludedInMultiSelection)
      ) {
        api.workflows
          .getWorkflow(item.id)
          .then((data: any) => {
            multipleWorkflowsSelection(data)
            setOptionsLoaded(true)
          })
          .catch((error: any) => {
            console.error('Failed to load workflow options for multi-selection:', error)
            setOptionsLoaded(true)
          })
      } else {
        api.workflows
          .getWorkflow(item.id)
          .then((data: any) => {
            setOptions(data)
            setOptionsLoaded(true)
          })
          .catch((error: any) => {
            console.error('Failed to load workflow options:', error)
            setOptionsLoaded(true)
          })
      }
    }
  }

  return <ActionsMenu items={items} menuId={`workflow-${item.id}`} onToggle={handleMenuToggle} isBigMenu={false} />
}

export const WorkflowsTable: React.FC<WorkflowsTableProps> = ({ isEosEnabled }) => {
  const {
    service: {
      filter: { translate, date }
    }
  } = useServiceContext()
  const tableData = useRcmTableData()
  const { showModal } = useModal()

  useEffect(() => {
    // Load data using context
    tableData.refreshTable()
  }, []) // Empty dependency array to run only on mount

  function getCurrentPhase(flow: Workflow) {
    if (flow.status === WorkflowStatus.CANCELLED) {
      return translate('WORKFLOWS_STEP_STATUS_CANCELLED')
    } else if (flow.status === WorkflowStatus.FINISHED) {
      return translate('WORKFLOWS_STEP_STATUS_FINISHED')
    }
    return flow.currentPhase
  }

  if (isEosEnabled) {
    return (
      <WorkflowsTableFull
        hidePaginate={true}
        disableOnInitLoad={true}
        isEosEnabled={isEosEnabled}
        viewType=""
        TableFooter={
          <tr>
            <td colSpan={9} className="view-all">
              <Link to="/ChangeRequests/active">
                {translate('VIEW_ALL')} ({tableData.total > 500 ? '500+' : tableData.total})
              </Link>
            </td>
          </tr>
        }
      />
    )
  }

  return (
    <RcmTable
      className="workflow-list-table chita-table dashboard-table"
      noResultsText="NO_RESULTS_FILTERED"
      hidePaginate={true}
      disableOnInitLoad={false}
      TableFooter={
        <tr>
          <td colSpan={8} className="view-all">
            <Link to="/ChangeRequests/active">
              {translate('VIEW_ALL')} ({tableData.total > 500 ? '500+' : tableData.total})
            </Link>
          </td>
        </tr>
      }
      HeadRow={
        <TableHeadRow>
          <TableHeadCell id="3f32b004-4a50-45fe-b94c-2b033d60a97a">{translate('NUMBER')}</TableHeadCell>
          <TableHeadCell id="b6476be7-628a-4566-a041-0e56feb2736e">{translate('DESCRIPTION')}</TableHeadCell>
          <TableHeadCell id="bf5bdf3b-018b-4f13-a55c-169c45eb3e71">{translate('STATUS')}</TableHeadCell>
          <TableHeadCell id="d1ef2677-262f-4b6f-9af8-0106bf58b839">{translate('DOCUMENT_NAME')}</TableHeadCell>
          <TableHeadCell id="0e4f1191-e2d3-4f0c-9c90-6097b991fe3b">{translate('START_DATE')}</TableHeadCell>
          <TableHeadCell id="9bba311e-00aa-4205-8646-b695e6fcbc42">{translate('INITIATOR')}</TableHeadCell>
          <TableHeadCell id="ea755526-5c6a-4582-ade2-ce83f7989324">{translate('CURRENT_PHASE')}</TableHeadCell>
          <TableHeadCell id="f88a1091-4d87-4d9d-b509-00e74575a61a" className="actions">
            {translate('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={(props) => {
        const row = props.row

        return (
          <TableRow {...props}>
            <td>
              <button
                type="button"
                className="text-link-btn word-break btn btn-link"
                onClick={() => {
                  showModal(<WorkflowDetailsModal wfId={row.id} />)
                }}
              >
                {row.formattedId}
              </button>
            </td>
            <td>
              <button
                type="button"
                className="text-link-btn word-break btn btn-link"
                title={row.localizedType}
                onClick={() => {
                  showModal(<WorkflowDetailsModal wfId={row.id} />)
                }}
              >
                {row.localizedType}
              </button>
            </td>
            <td className={'status'}>
              <div
                onClick={() => {
                  showModal(<WorkflowDetailsModal wfId={row.id} />)
                }}
                className={
                  'status-wrapper' +
                  (row.status === WorkflowStatus.CANCELLED || row.status == WorkflowStatus.SUSPENDED || row.rejected
                    ? ' canceled'
                    : '')
                }
              >
                <span style={{ width: row.progress + '%' }} className={row.progress == 100 ? 'w100p' : ''}></span>
              </div>
            </td>
            <td className="doc-name clipped-cell">
              <TaskDocLink task={row as any} isWorkflow />
            </td>
            <td>{date(row.startDate)}</td>
            <td>{row.initiator?.name || 'Unknown'}</td>
            <td>{getCurrentPhase(row)}</td>
            <td className="actions">
              <WorkflowTableMenu item={row} />
            </td>
          </TableRow>
        )
      }}
    />
  )
}
